
import React, { useState, useCallback, useEffect, useRef, Suspense } from 'react';
import { User } from './types';
import * as authService from './services/authService';
import { getBackendUrl } from './services/api';
import PublicHomePage from './components/PublicHomePage';
import { Router, useRouter } from './components/Router';
import { loadRecaptcha } from './utils/recaptcha';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy load heavy components to improve TBT and LCP
const AuthContainer = React.lazy(() => import('./components/auth/AuthContainer'));
const Login = React.lazy(() => import('./components/auth/Login'));
const Register = React.lazy(() => import('./components/auth/Register'));
const ForgotPassword = React.lazy(() => import('./components/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./components/auth/ResetPassword'));
const MainApp = React.lazy(() => import('./components/MainApp'));
const AdminPanel = React.lazy(() => import('./components/admin/AdminPanel'));
const GoogleCallback = React.lazy(() => import('./components/GoogleCallback'));
const PrivacyPolicyPage = React.lazy(() => import('./components/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./components/TermsOfServicePage'));
import PricingPage from './components/PricingPage';
const DocumentationPage = React.lazy(() => import('./components/DocumentationPage'));
const LeadGenView = React.lazy(() => import('./components/LeadGenView'));
const PublicLeadForm = React.lazy(() => import('./components/public/PublicLeadForm'));
const PublicAgentForm = React.lazy(() => import('./components/public/PublicAgentForm'));
const WebChatWidget = React.lazy(() => import('./components/public/WebChatWidget'));
const ProfileDropdown = React.lazy(() => import('./components/ProfileDropdown'));
const BlogGoogleSheetsWhatsApp = React.lazy(() => import('./components/blog/BlogGoogleSheetsWhatsApp'));
const BlogQualifyWhatsAppLeads = React.lazy(() => import('./components/blog/BlogQualifyWhatsAppLeads'));
const BlogWhatsAppSalesFunnels = React.lazy(() => import('./components/blog/BlogWhatsAppSalesFunnels'));
const BlogPhoneLookupDataSync = React.lazy(() => import('./components/blog/BlogPhoneLookupDataSync'));
import BlogIndex from './components/blog/BlogIndex';
import WhatsAppAutomationNigeriaPage from './components/WhatsAppAutomationNigeriaPage';
import NigeriaPlaybookPage from './components/blog/NigeriaPlaybookPage';
import SEO from './components/SEO';

const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center bg-[#0c1427] text-white">
        <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full"></div>
    </div>
);

// Simple router based on pathname
function AppRoutes({ installPrompt, onInstall }: { installPrompt: any, onInstall: () => void }) {
    const { path, navigate } = useRouter();
    const pathname = path.split('?')[0].replace(/\/$/, '') || '/';

    // Lazy load routes inside Suspense with ErrorBoundary protection
    return (
        <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
                {(() => {
                    if (pathname === '/google-callback') return <GoogleCallback />;
                    if (pathname === '/privacy-policy') return <PrivacyPolicyPage />;
                    if (pathname === '/terms-of-service') return <TermsOfServicePage />;
                    if (pathname === '/pricing') return <PricingPage />;
                    if (pathname === '/docs') return <DocumentationPage />;
                    if (pathname === '/blog/whatsapp-api-pricing-nigeria') {
                        return <NigeriaPlaybookPage slug="whatsapp-api-pricing-nigeria" />;
                    }
                    if (pathname === '/blog/wasender-gbwhatsapp-ban-official-meta-api-nigeria') {
                        return <NigeriaPlaybookPage slug="wasender-gbwhatsapp-ban-official-meta-api-nigeria" />;
                    }
                    if (pathname === '/blog/paystack-whatsapp-order-automation-google-sheets-nigeria') {
                        return <NigeriaPlaybookPage slug="paystack-whatsapp-order-automation-google-sheets-nigeria" />;
                    }
                    if (pathname === '/blog/whatsapp-phone-verification-sheets') {
                        return (
                            <>
                                <SEO
                                    title="Zero-Drop WhatsApp Verification: Check Phone Numbers Against Google Sheets"
                                    description="Learn how Nigerian and global engineering teams query Google Sheets in 280ms to verify +234 customer records, pull dynamic VIP balances, and personalize WhatsApp chats."
                                    keywords="WhatsApp Google Sheets Phone Lookup, Verify WhatsApp Numbers Nigeria, Two-Way Google Sheets WhatsApp Sync"
                                    canonical="https://bytecohortreach.com/blog/whatsapp-phone-verification-sheets"
                                    type="article"
                                    publishedTime="2026-09-12T08:00:00+01:00"
                                />
                                <BlogPhoneLookupDataSync />
                            </>
                        );
                    }
                    if (pathname === '/blog/whatsapp-google-sheets-sync') {
                        return (
                            <>
                                <SEO
                                    title="Automate Google Sheets WhatsApp Sync: Real-Time Bidirectional Workflow"
                                    description="Connect your Google Sheets directly to the official Meta WhatsApp Cloud API in Nigeria. Automatically log incoming leads, update order statuses, and trigger bulk campaigns."
                                    keywords="Automate Google Sheets WhatsApp Nigeria, Bidirectional WhatsApp CRM Sync, Meta Cloud API Google Sheets"
                                    canonical="https://bytecohortreach.com/blog/whatsapp-google-sheets-sync"
                                    type="article"
                                    publishedTime="2026-08-20T08:00:00+01:00"
                                />
                                <BlogGoogleSheetsWhatsApp />
                            </>
                        );
                    }
                    if (pathname === '/blog/qualify-whatsapp-leads') {
                        return (
                            <>
                                <SEO
                                    title="How to Qualify Inbound WhatsApp Leads with Autonomous AI Agents"
                                    description="Deploy Google Gemini AI agents on WhatsApp to score customer intent, answer Nigerian pricing & delivery FAQs 24/7, and hand off warm buyers to human sales reps."
                                    keywords="WhatsApp AI Lead Qualification Nigeria, Gemini AI WhatsApp Chatbot, Automated WhatsApp Sales Agent"
                                    canonical="https://bytecohortreach.com/blog/qualify-whatsapp-leads"
                                    type="article"
                                    publishedTime="2026-08-05T08:00:00+01:00"
                                />
                                <BlogQualifyWhatsAppLeads />
                            </>
                        );
                    }
                    if (pathname === '/blog/whatsapp-sales-funnel') {
                        return (
                            <>
                                <SEO
                                    title="WhatsApp Sales Funnel Architecture: Step-by-Step Blueprint to 4.8x Conversions"
                                    description="Replace low-open email campaigns with 98% open rate WhatsApp sales funnels in Nigeria. Combine Click-to-WhatsApp ads, Gemini AI triage, and Paystack checkout links."
                                    keywords="WhatsApp Sales Funnel Nigeria, Click to WhatsApp Ads Nigeria, WhatsApp Marketing Conversion Blueprint"
                                    canonical="https://bytecohortreach.com/blog/whatsapp-sales-funnel"
                                    type="article"
                                    publishedTime="2026-08-28T08:00:00+01:00"
                                />
                                <BlogWhatsAppSalesFunnels />
                            </>
                        );
                    }
                    if (pathname === '/blog/whatsapp-automation-nigeria' || pathname === '/whatsapp-automation-nigeria') {
                        return <WhatsAppAutomationNigeriaPage />;
                    }
                    if (pathname === '/blog') return <BlogIndex />;
                    if (pathname === '/reset-password') return <div className="themed-background min-h-screen"><AuthContainer><ResetPassword onShowLogin={() => { navigate('/app'); }} /></AuthContainer></div>;
                    if (pathname.startsWith('/p/')) return <PublicLeadForm />;
                    if (pathname.startsWith('/public/forms/') || pathname.startsWith('/public/form/') || pathname.startsWith('/f/')) return <PublicAgentForm />;
                    if (pathname.startsWith('/embed/chat/')) return <WebChatWidget />;
                    if (pathname.startsWith('/app')) return <div className="themed-background min-h-screen"><AppContent installPrompt={installPrompt} onInstall={onInstall} /></div>;
                    return <PublicHomePage />;
                })()}
            </Suspense>
        </ErrorBoundary>
    );
}

function AppContent({ installPrompt, onInstall }: { installPrompt: any, onInstall: () => void }) {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [authView, setAuthView] = useState<'login' | 'register' | 'forgot' | 'reset'>('login');
    const [isLoading, setIsLoading] = useState(true);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const [googleClientId, setGoogleClientId] = useState<string | null>(null);
    const [isImpersonating, setIsImpersonating] = useState(!!localStorage.getItem('admin_return_token'));
    const [verificationMessage, setVerificationMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
    const { path, navigate } = useRouter();
    const refreshedRef = useRef(false);
    
    const pathname = path.split('?')[0];

    const onUserRefresh = useCallback(async () => {
        try {
            const user = await authService.verifySession();
            setCurrentUser(user);
        } catch (error) {
            console.error("Failed to refresh user session:", error);
            setCurrentUser(null);
            setIsImpersonating(false);
            localStorage.removeItem('admin_return_token');
            localStorage.removeItem('whatsapp_sender_token');
        }
    }, []);

    // Effect for initial app load: fetch config AND verify session
    useEffect(() => {
        const initializeApp = async () => {
            setIsLoading(true);
            
            // Initialize reCAPTCHA on load to satisfy "Request tokens" requirement
            loadRecaptcha();

            try {
                const backendUrl = getBackendUrl();
                if (!backendUrl) {
                    throw new Error("Backend URL has not been configured. Please edit the BACKEND_URL constant in `services/api` to point to your deployed backend service.");
                }

                const config = await authService.getAppConfig();
                setGoogleClientId(config.googleClientId);
                
                // Verification & Reset Password Token Logic
                const hashParts = window.location.hash.split('?');
                const params = new URLSearchParams(hashParts[1] || window.location.search);
                const verifyToken = params.get('verify_token');
                const resetToken = params.get('reset_token') || (pathname === '/reset-password' ? params.get('token') : null);

                if (verifyToken) {
                    try {
                        const res = await authService.verifyEmail(verifyToken);
                        setVerificationMessage({ type: 'success', text: res.message || 'Email verified successfully! Please log in.' });
                        // Clean URL to remove token
                        window.history.replaceState(null, '', window.location.pathname + window.location.hash.split('?')[0]);
                        // Ensure login view is shown
                        setAuthView('login');
                    } catch (err: any) {
                        setVerificationMessage({ type: 'error', text: err.message || 'Verification failed. The link may be expired.' });
                    }
                } else if (resetToken) {
                    setAuthView('reset');
                }

                const user = await authService.verifySession();
                setCurrentUser(user);

                // Check impersonation status after verifying session
                setIsImpersonating(!!localStorage.getItem('admin_return_token'));
                setConnectionError(null);
            } catch (error: any) {
                console.error("App Initialization Failed:", error);
                if (error.message.includes("Backend URL has not been configured")) {
                    setConnectionError(error.message);
                } else if (error instanceof TypeError || error.message.includes('fetch')) {
                    setConnectionError("Could not connect to the backend. Please ensure the backend service is running, accessible, and the URL in `services/api` is correct.");
                } else {
                    setCurrentUser(null);
                    setIsImpersonating(false);
                    localStorage.removeItem('admin_return_token');
                }
            } finally {
                setIsLoading(false);
            }
        };

        initializeApp();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const needsRefresh = params.get('refresh_user') === 'true';

        if (needsRefresh && !refreshedRef.current) {
            refreshedRef.current = true;
            onUserRefresh();
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [onUserRefresh]);


    const handleLoginSuccess = useCallback((user: User) => {
        setCurrentUser(user);
        
        // Handle post-login redirection
        // Check hash for query params since we use hash routing
        const hashPath = window.location.hash.substring(1); // e.g., /app?redirect=pricing
        const queryIndex = hashPath.indexOf('?');
        
        if (queryIndex !== -1) {
            const search = hashPath.substring(queryIndex);
            const params = new URLSearchParams(search);
            const redirect = params.get('redirect');
            if (redirect === 'pricing') {
                navigate('/pricing');
                return;
            }
        }
        
        // Fallback check on window.location.search just in case
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.get('redirect') === 'pricing') {
             navigate('/pricing');
             return;
        }

        if (user.role === 'admin') {
            navigate('/app/admin');
        } else {
            navigate('/app');
        }
    }, [navigate]);

    const handleReturnToAdmin = useCallback(() => {
        const adminToken = localStorage.getItem('admin_return_token');
        if (adminToken) {
            localStorage.setItem('whatsapp_sender_token', adminToken);
            localStorage.removeItem('admin_return_token');
            // By doing a full navigation and reload, we bypass component state issues
            // and let the initial auth check correctly identify the admin user.
            window.location.hash = '/app/admin';
            window.location.reload();
        }
    }, []);

    useEffect(() => {
        if (!currentUser) return;

        const isOnAdminPath = pathname.startsWith('/app/admin');

        if (currentUser.role === 'admin' && !isImpersonating) {
            // Allow admin to access both /app and /app/admin
        } else { // Regular user OR impersonating admin
            if (isOnAdminPath) {
                navigate('/app');
            }
            
            // --- AGENT REDIRECT LOGIC ---
            // If the user is an 'agent' (not an owner/manager) and they land on the root /app (dashboard),
            // redirect them immediately to the inbox.
            const isRestrictedRole = currentUser.orgRole === 'agent';
            if (isRestrictedRole && (pathname === '/app' || pathname === '/app/')) {
                navigate('/app/inbox');
            }
        }
    }, [currentUser, isImpersonating, pathname, navigate]);

    const handleLogout = useCallback(() => {
        authService.logout();
        localStorage.removeItem('admin_return_token'); // Clear impersonation on logout
        setCurrentUser(null);
        setAuthView('login');
        setIsImpersonating(false);
        navigate('/');
    }, [navigate]);

    const renderAuthView = () => {
        switch (authView) {
            case 'register':
                return <Register onLoginSuccess={handleLoginSuccess} onShowLogin={() => setAuthView('login')} googleClientId={googleClientId} />;
            case 'forgot':
                return <ForgotPassword onShowLogin={() => setAuthView('login')} />;
            case 'reset':
                return <ResetPassword onShowLogin={() => setAuthView('login')} />;
            case 'login':
            default:
                return (
                    <Login 
                        onLoginSuccess={handleLoginSuccess} 
                        onShowRegister={() => setAuthView('register')} 
                        onShowForgotPassword={() => setAuthView('forgot')}
                        googleClientId={googleClientId}
                    />
                );
        }
    };

    if (isLoading) {
        return <PageLoader />;
    }

    if (connectionError) {
        return <div className="min-h-screen flex items-center justify-center text-red-400 p-4 text-center">{connectionError}</div>;
    }

    if (!currentUser) {
        return (
            <AuthContainer>
                 {verificationMessage && (
                    <div className={`mb-6 p-4 rounded-lg border text-sm font-medium ${verificationMessage.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
                        {verificationMessage.text}
                    </div>
                )}
                {renderAuthView()}
            </AuthContainer>
        );
    }

    if (currentUser.role === 'admin' && !isImpersonating && pathname.startsWith('/app/admin')) {
        return <AdminPanel user={currentUser} onLogout={handleLogout} />;
    }

    // LeadGenView Routing Logic inside AppContent to handle the 'Growth Tools' view
    if (pathname.startsWith('/app/lead-gen')) {
        return (
            <div className="flex h-screen bg-[#030712] text-gray-200 overflow-hidden">
                <div className="flex-1 flex flex-col min-w-0">
                    <header className="bg-transparent flex-shrink-0 border-b border-white/5 backdrop-blur-sm bg-black/10 relative z-30">
                        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <a href="#/app" onClick={(e) => { e.preventDefault(); navigate('/app');}} className="text-xl font-bold text-white tracking-tight">Bytecohort Reach</a>
                            </div>
                            <div className="flex items-center space-x-4">
                                <button onClick={() => navigate('/app')} className="text-sm text-gray-300 hover:text-white">Dashboard</button>
                                <ProfileDropdown 
                                    user={currentUser} 
                                    onLogout={handleLogout} 
                                    installPrompt={installPrompt} 
                                    onInstall={onInstall} 
                                />
                            </div>
                        </div>
                    </header>
                    <main className="flex-1 min-h-0 relative overflow-hidden flex flex-col">
                        <LeadGenView onBack={() => navigate('/app')} />
                    </main>
                </div>
            </div>
        );
    }

    return (
        <MainApp 
            user={currentUser} 
            onLogout={handleLogout} 
            onUserRefresh={onUserRefresh}
            isImpersonating={isImpersonating}
            onReturnToAdmin={handleReturnToAdmin}
            installPrompt={installPrompt}
            onInstall={onInstall}
        />
    );
}

export default function App() {
    const [installPrompt, setInstallPrompt] = useState<any>(null);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e: any) => {
            e.preventDefault();
            setInstallPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstall = async () => {
        if (!installPrompt) return;
        installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') {
            setInstallPrompt(null);
        }
    };

    return (
        <ErrorBoundary>
            <Router>
                <AppRoutes installPrompt={installPrompt} onInstall={handleInstall} />
            </Router>
        </ErrorBoundary>
    );
}


